import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Assignment } from "./CallProspect";
import { CallProspect, NO_ASSIGNMENT, pendingLink } from "./CallProspect";
import type { CallOutcome } from "./queries";
import { useCall, useLinkCall, useSaveCallNotes } from "./queries";
import { awaitsSummary, useSoftphoneStore } from "./store";

/** The six outcomes on `Call.Outcome`, in the order an agent thinks of them. */
const OUTCOMES: CallOutcome[] = [1, 2, 3, 4, 5, 6];

/**
 * The shortest summary the write-up saves — `NOTES_MIN_LENGTH` on the server,
 * which refuses anything shorter too. Enough to rule out "ok" and "listo",
 * which tell the next seller nothing about the call.
 */
const NOTES_MIN_LENGTH = 10;

/**
 * What the agent fills in after hanging up.
 *
 * Two jobs in one form, because they happen at the same moment and neither is
 * worth a screen of its own:
 *
 * 1. The write-up. Outcome and notes are what put the call on the client's
 *    timeline (D9) — without this the call log is a list of durations. The
 *    notes are required, and at least `NOTES_MIN_LENGTH` characters long. On
 *    a call the client answered there is no *Cerrar* either: something was
 *    said, and saving the summary is the only way to put the bar away. A call
 *    that never connected keeps it — there is nothing to tell about a busy
 *    signal beyond what the timeline already records.
 * 2. Consent, when the agent asked (.docs/06 §2.1, mechanism 3). The agents
 *    are calling these contacts anyway, on recorded lines. Ten seconds of that
 *    call turns a legacy row with `consents_to_whatsapp = null` into one with
 *    a recording behind it, and over a few months that is how fifteen years of
 *    contacts become messageable — with evidence, rather than by blasting the
 *    base with a template.
 *
 * A third, on a call dialled from *Llamar* only: the number belonged to nobody
 * on file, and this is the moment to say who it was — an existing company and
 * contact, chosen, or new ones, registered (`CallProspect`). Before the
 * write-up rather than after, because saving the write-up closes the bar — and
 * once there is a contact the consent controls below have somebody to record
 * an answer against. What was chosen is filed by the save itself, first, so
 * the consent answers that follow land on that contact.
 *
 * The consent controls are tri-state and start at "no preguntado". A checkbox
 * cannot say "I asked and they said no", which is a different fact from not
 * having asked and is the one the recording pipeline reads before recording
 * the next call.
 */
export function CallWrapUp({ callRef }: { callRef: string }) {
  const { t } = useTranslation();
  const clear = useSoftphoneStore((state) => state.clear);
  const fromKeypad = useSoftphoneStore((state) => state.call?.keypad ?? false);
  const answered = useSoftphoneStore(
    (state) => state.call?.ref === callRef && awaitsSummary(state.call),
  );
  const save = useSaveCallNotes();
  const link = useLinkCall();
  // Generated, not literal: the bar is in the shell and, while an event is
  // open, in its modal too, and two forms cannot share an id.
  const outcomeId = useId();
  const notesId = useId();
  // The store knows what was dialled; only the server knows whether the call
  // ended up attached to a contact, and consent has nowhere to go without one.
  const { data: call } = useCall(callRef);

  const [outcome, setOutcome] = useState<CallOutcome | "">("");
  const [notes, setNotes] = useState("");
  const [notesError, setNotesError] = useState<string | null>(null);
  const [consentWhatsapp, setConsentWhatsapp] = useState<boolean | null>(null);
  const [consentRecording, setConsentRecording] = useState<boolean | null>(null);
  const [assignment, setAssignment] = useState<Assignment>(NO_ASSIGNMENT);

  // What a keypad call is still to be filed under, chosen in `CallProspect`.
  const toLink = fromKeypad && call ? pendingLink(call, assignment) : null;
  const hasContact = call?.contact != null || (toLink?.contact ?? null) !== null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (notes.trim().length < NOTES_MIN_LENGTH) {
      setNotesError(t("call.notesTooShort", { min: NOTES_MIN_LENGTH }));
      return;
    }
    setNotesError(null);
    if (toLink) {
      try {
        await link.mutateAsync({ ref: callRef, ...toLink });
      } catch {
        return; // Said below, from `link.error`; nothing else is saved.
      }
    }
    save.mutate(
      {
        ref: callRef,
        outcome: outcome === "" ? null : outcome,
        notes,
        // Never send an answer the form could not have collected — the server
        // refuses consent on a call with no contact, and that 400 would land
        // on an agent who never touched the control.
        consentWhatsapp: hasContact ? consentWhatsapp : null,
        consentRecording: hasContact ? consentRecording : null,
      },
      { onSuccess: () => clear() },
    );
  }

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="w-full space-y-3 border-2 border-ground/25 bg-ink/25 p-3"
      aria-label={t("call.wrapUp")}
    >
      <div className="flex flex-wrap gap-2">
        <label className="sr-only" htmlFor={outcomeId}>
          {t("call.outcome")}
        </label>
        <select
          id={outcomeId}
          value={outcome}
          onChange={(event) =>
            setOutcome(
              event.target.value === ""
                ? ""
                : (Number(event.target.value) as CallOutcome),
            )
          }
          className="border-2 border-ground/40 bg-transparent px-2 py-1.5 text-ground"
        >
          <option value="">{t("call.outcomePrompt")}</option>
          {OUTCOMES.map((value) => (
            <option key={value} value={value} className="text-ink">
              {t(`call.outcomes.${value}`)}
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor={notesId}>
          {t("call.notes")}
        </label>
        <input
          id={notesId}
          value={notes}
          onChange={(event) => {
            setNotes(event.target.value);
            // Gone as soon as it no longer applies, not on the next click.
            if (event.target.value.trim().length >= NOTES_MIN_LENGTH) {
              setNotesError(null);
            }
          }}
          aria-required="true"
          aria-invalid={notesError !== null}
          placeholder={t("call.notesPlaceholder", { min: NOTES_MIN_LENGTH })}
          // A floor, so that beside the select on a narrow bar — a phone, or
          // the event modal — it wraps to a row of its own instead of
          // shrinking to a few characters.
          className="min-w-[12rem] flex-1 border-2 border-ground/40 bg-transparent px-2 py-1.5 text-ground placeholder:text-ground/50"
        />
      </div>

      {fromKeypad && call ? (
        <CallProspect call={call} assignment={assignment} onAssign={setAssignment} />
      ) : null}

      {hasContact ? (
        <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <legend className="sr-only">{t("call.consentLegend")}</legend>
          <span className="text-ground/70">{t("call.consentAsked")}</span>
          <TriState
            label={t("call.consentWhatsapp")}
            value={consentWhatsapp}
            onChange={setConsentWhatsapp}
          />
          <TriState
            label={t("call.consentRecording")}
            value={consentRecording}
            onChange={setConsentRecording}
          />
        </fieldset>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={save.isPending || link.isPending}
          className="bg-ground px-3 py-1.5 font-display font-extrabold text-ink hover:bg-mute-200 disabled:opacity-50"
        >
          {t("call.saveWrapUp")}
        </button>
        {answered ? (
          // Said where the button would be, so its absence is not a puzzle.
          <span className="text-ground/70">{t("call.answeredMustSave")}</span>
        ) : (
          <button
            type="button"
            onClick={clear}
            className="border-2 border-ground/40 px-3 py-1.5 text-ground hover:bg-ground/15"
          >
            {t("call.dismiss")}
          </button>
        )}
        {notesError ? (
          <span role="alert" className="font-bold text-ground">
            {notesError}
          </span>
        ) : link.isError ? (
          <span role="alert" className="text-ground/90">
            {link.error.message}
          </span>
        ) : save.isError ? (
          <span role="alert" className="text-ground/90">
            {save.error.message}
          </span>
        ) : null}
      </div>
    </form>
  );
}

/**
 * Sí / No / not asked, as a three-way radio group.
 *
 * A checkbox would collapse "they said no" into "I did not ask", and those are
 * different facts: the first silences future recording and messaging, the
 * second leaves the question open for the next call (.docs/06 §2.1).
 */
function TriState({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (next: boolean | null) => void;
}) {
  const { t } = useTranslation();
  const options: { key: string; answer: boolean | null }[] = [
    { key: "call.consentUnasked", answer: null },
    { key: "call.consentYes", answer: true },
    { key: "call.consentNo", answer: false },
  ];
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-ground/90">{label}</span>
      <div role="group" aria-label={label} className="flex gap-1">
        {options.map(({ key, answer }) => (
          <button
            key={key}
            type="button"
            aria-pressed={value === answer}
            onClick={() => onChange(answer)}
            // The `.seg` of the system, redrawn for a dark ground: the
            // component's own colours assume the light one, and this control
            // lives inside the call bar.
            className={`border-2 px-2 py-1 ${
              value === answer
                ? "border-ground bg-ground text-ink"
                : "border-ground/40 text-ground hover:bg-ground/15"
            }`}
          >
            {t(key)}
          </button>
        ))}
      </div>
    </div>
  );
}
